import {
  getClipKey,
  useClipSort,
} from '@src/components/ClipProducer/useClipSort';
import { highlightRangesToPlaybackRanges } from '@src/utility/transcriptQuoteUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ClipFragment } from '@src/services/AIClipProducer';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';

/**
 * Candidate rail colours (PLAT-11806 DS6).
 *
 * Neutral on purpose. Every *hue* on this surface already carries a meaning —
 * green clips, blue quotes and overlays, seven saved-segment colours, seven
 * topic colours, amber speakers/waveform/active-tab — so an eighth would read
 * as one of those. What separates a candidate is the channel it uses: saved
 * things underline the words, a candidate paints the left gutter rail, and the
 * rail's own muted/solid pair (`topicRailCss`) does the rest.
 */
const CANDIDATE_RAIL_COLOR = 'var(--text-muted)';
const CANDIDATE_RAIL_ACTIVE_COLOR = 'var(--foreground)';

/** Options shown before "show the rest" — see PLAT-11806 DS7. */
export const DEFAULT_CANDIDATE_LIMIT = 3;

export interface UseCandidateHighlightsInput {
  /** Every candidate the current search produced, unranked. */
  candidates: ClipFragment[];
  /** The array the candidates' `transcriptPosition` indices point into. */
  transcriptElements: TranscriptElement[] | undefined;
  /**
   * Identifies the search these candidates belong to. Changes exactly once
   * per question asked.
   *
   * Resetting on `candidates` instead would be wrong twice over: the array
   * gets a new identity on every streamed chunk (so a selection made while
   * results were still arriving would be thrown away), and a caller that
   * rebuilds the array inline would re-fire the reset on every render —
   * CLAUDE.md pitfall 18.
   */
  searchId: string | null;
}

export interface CandidateView {
  key: string;
  fragment: ClipFragment;
  /** 1-based rank, matching the badge in the transcript gutter and the card. */
  rank: number;
  isSelected: boolean;
}

export interface UseCandidateHighlightsResult {
  /** Ranked, then trimmed to `DEFAULT_CANDIDATE_LIMIT` unless expanded. */
  visibleCandidates: CandidateView[];
  totalCandidates: number;
  expanded: boolean;
  toggleExpanded: () => void;

  selectedKey: string | null;
  selectCandidate: (key: string | null) => void;

  showOnly: boolean;
  setShowOnly: (next: boolean) => void;

  /** True while a search's results own the transcript's highlight props. */
  active: boolean;

  // ── <Transcript> props (the same four the topic filter drives) ──
  visibleParagraphIds: ReadonlySet<string> | undefined;
  highlightedParagraphIds: ReadonlySet<string> | undefined;
  paragraphHighlightColors: ReadonlyMap<string, string> | undefined;
  activeParagraphId: string | undefined;

  /**
   * Time windows for the docked player's `playbackSegments`. The selected
   * candidate alone when one is picked, otherwise every visible candidate —
   * which is all "Play All" is (PLAT-11806 DS4).
   */
  playbackRanges: { start: number; end: number }[] | null;
}

/**
 * Turns a search's candidates into the transcript props and playback windows
 * that already exist on this surface (PLAT-11806).
 *
 * Host-owned, mirroring `useTopicFiltering`: `Transcript.tsx` has four hosts
 * and a size/prop ratchet, so a feature only this host needs is composed from
 * the outside through props it already accepts — never a new prop on the
 * shared component (CLAUDE.md guardrail 5).
 *
 * Ranking is not re-derived here. `useClipSort` already sorts by the
 * producer's impact score, and the producer has already dropped
 * near-duplicates, so this hook only trims the ranked list.
 *
 * Pure React state — no MobX, no IO.
 */
export function useCandidateHighlights(
  input: UseCandidateHighlightsInput,
): UseCandidateHighlightsResult {
  const { candidates, transcriptElements, searchId } = input;

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showOnly, setShowOnly] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Impact-score-first ordering, straight from the Clip Producer's hook.
  // 'all': this hook does its own trimming, and Show More must reach the last.
  const { sortedClips } = useClipSort(candidates, 'all');

  // A new question replaces the old answer wholesale: a key carried over from
  // the previous search would point at a passage the user never asked about.
  useEffect(() => {
    setSelectedKey(null);
    setExpanded(false);
    setShowOnly(false);
  }, [searchId]);

  const visibleCandidates = useMemo<CandidateView[]>(() => {
    const shown = expanded
      ? sortedClips
      : sortedClips.slice(0, DEFAULT_CANDIDATE_LIMIT);
    return shown.map((fragment, index) => {
      const key = getClipKey(fragment);
      return {
        key,
        fragment,
        rank: index + 1,
        isSelected: key === selectedKey,
      };
    });
  }, [sortedClips, expanded, selectedKey]);

  const active = visibleCandidates.length > 0;

  const toggleExpanded = useCallback(() => setExpanded(prev => !prev), []);

  const selectCandidate = useCallback((key: string | null) => {
    // Clicking the selected card again clears it, which is also how you get
    // back from "play this one" to "play all".
    setSelectedKey(prev => (prev === key ? null : key));
  }, []);

  /** Element-index ranges, in rank order. */
  const rangesByKey = useMemo(() => {
    const map = new Map<string, HighlightRange>();
    for (const candidate of visibleCandidates) {
      const { startIndex, endIndex } = candidate.fragment.transcriptPosition;
      if (startIndex == null || endIndex == null) continue;
      map.set(candidate.key, {
        id: candidate.key,
        start: startIndex,
        end: endIndex,
      });
    }
    return map;
  }, [visibleCandidates]);

  /** Paragraph ids each candidate covers, keyed by candidate. */
  const paragraphIdsByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!transcriptElements) return map;
    for (const [key, range] of rangesByKey) {
      const ids: string[] = [];
      const seen = new Set<string>();
      const last = Math.min(range.end, transcriptElements.length - 1);
      for (let i = Math.max(0, range.start); i <= last; i++) {
        const pid = transcriptElements[i]?.paragraph_id;
        if (pid && !seen.has(pid)) {
          seen.add(pid);
          ids.push(pid);
        }
      }
      map.set(key, ids);
    }
    return map;
  }, [rangesByKey, transcriptElements]);

  const highlightedParagraphIds = useMemo(() => {
    if (!active) return undefined;
    const ids = new Set<string>();
    for (const pids of paragraphIdsByKey.values()) {
      for (const pid of pids) ids.add(pid);
    }
    return ids;
  }, [active, paragraphIdsByKey]);

  const paragraphHighlightColors = useMemo(() => {
    if (!active) return undefined;
    const colors = new Map<string, string>();
    for (const candidate of visibleCandidates) {
      const color = candidate.isSelected
        ? CANDIDATE_RAIL_ACTIVE_COLOR
        : CANDIDATE_RAIL_COLOR;
      for (const pid of paragraphIdsByKey.get(candidate.key) ?? []) {
        // Overlapping candidates are already deduped by the producer, but a
        // selected one still wins any paragraph it shares.
        if (candidate.isSelected || !colors.has(pid)) colors.set(pid, color);
      }
    }
    return colors;
  }, [active, visibleCandidates, paragraphIdsByKey]);

  // The rail's solid state, and what the prev/next-style scroll lands on.
  // Falls back to the top-ranked candidate so a fresh result set opens on its
  // best answer rather than on nothing.
  const activeParagraphId = useMemo(() => {
    if (!active) return undefined;
    const target =
      visibleCandidates.find(c => c.isSelected) ?? visibleCandidates[0];
    return paragraphIdsByKey.get(target.key)?.[0];
  }, [active, visibleCandidates, paragraphIdsByKey]);

  const visibleParagraphIds = useMemo(() => {
    if (!active || !showOnly) return undefined;
    // An empty set would hide the whole transcript, which is never what the
    // user meant by Show Only.
    return highlightedParagraphIds?.size ? highlightedParagraphIds : undefined;
  }, [active, showOnly, highlightedParagraphIds]);

  const playbackRanges = useMemo(() => {
    if (!active || !transcriptElements) return null;
    const selected = visibleCandidates.find(c => c.isSelected);
    const ranges = selected
      ? [rangesByKey.get(selected.key)].filter((r): r is HighlightRange => !!r)
      : Array.from(rangesByKey.values());
    if (ranges.length === 0) return null;
    const windows = highlightRangesToPlaybackRanges(ranges, transcriptElements);
    return windows.length > 0 ? windows : null;
  }, [active, visibleCandidates, rangesByKey, transcriptElements]);

  return {
    visibleCandidates,
    totalCandidates: sortedClips.length,
    expanded,
    toggleExpanded,
    selectedKey,
    selectCandidate,
    showOnly,
    setShowOnly,
    active,
    visibleParagraphIds,
    highlightedParagraphIds,
    paragraphHighlightColors,
    activeParagraphId,
    playbackRanges,
  };
}
